import React, { useRef, useEffect, useState } from 'react';
import {
  CellTemplate,
  Compatible,
  Uncertain,
  getCellProperty,
  isAlphaNumericKey,
  keyCodes
} from '@silevis/reactgrid';
import CellHoverButton from './CellHoverButton';
import './TextAreaCellTemplate.css';

export interface TextAreaCell {
  type: 'textArea';
  text: string;
  value?: number;
  className?: string;
  nonEditable?: boolean;
  triggerChar?: string;
  onRealTimeChange?: (text: string) => void;
  onEnrichCell?: () => void;
}

const TextAreaCellContent: React.FC<{ cell: Compatible<TextAreaCell> }> = ({ cell }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  const handleEnrichCell = () => {
    if (cell.onEnrichCell) {
      cell.onEnrichCell();
    }
  };

  return (
    <div 
      ref={cellRef}
      className={`rg-textarea-cell-content ${cell.className || ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative' }}
    >
      {cell.text}
      <CellHoverButton 
        onEnrich={handleEnrichCell}
        isVisible={isHovered && !cell.nonEditable}
      />
    </div>
  );
};

export const textAreaCellTemplate: CellTemplate<TextAreaCell> = {
  getCompatibleCell(uncertainCell: Uncertain<TextAreaCell>): Compatible<TextAreaCell> {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    const value = parseFloat(text) || 0;
    return { ...uncertainCell, text, value };
  },

  handleKeyDown(
    cell: Compatible<TextAreaCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean
  ): { cell: Compatible<TextAreaCell>; enableEditMode: boolean } {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
      // Convert keyCode to actual character
      let char = '';
      
      // Handle letters (A-Z)
      if (keyCode >= 65 && keyCode <= 90) {
        char = String.fromCharCode(keyCode);
        if (!shift) {
          char = char.toLowerCase();
        }
      }
      // Handle numbers and symbols (0-9)
      else if (keyCode >= 48 && keyCode <= 57) {
        if (shift) {
          // Shift + number keys for symbols
          const shiftSymbols = [')', '!', '@', '#', '$', '%', '^', '&', '*', '('];
          char = shiftSymbols[keyCode - 48];
        } else {
          char = String.fromCharCode(keyCode);
        }
      }
      // Handle other alphanumeric keys
      else {
        char = String.fromCharCode(keyCode);
      }
      
      const updatedCell = { ...cell, triggerChar: char };
      return { cell: updatedCell, enableEditMode: true };
    }
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  },

  update(cell: Compatible<TextAreaCell>, cellToMerge: any): Compatible<TextAreaCell> {
    return this.getCompatibleCell({ ...cell, text: cellToMerge.text });
  },

  render(
    cell: Compatible<TextAreaCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<TextAreaCell>, commit: boolean) => void
  ): React.ReactNode {
    if (!isInEditMode) {
      return <TextAreaCellContent cell={cell} />;
    }

    const template = this; // Store reference to template

    const TextAreaEditor = () => {
      const textareaRef = useRef<HTMLTextAreaElement>(null);

      // Use trigger character as initial value if available
      const initialValue = cell.triggerChar || cell.text;
      
      const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (!textarea || !textarea.style) return;

        // Reset height to calculate scroll height
        textarea.style.height = 'auto';
        
        // Calculate the content height
        const scrollHeight = textarea.scrollHeight;
        
        // Calculate max height based on distance to bottom of screen
        const textareaRect = textarea.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const distanceToBottom = viewportHeight - textareaRect.top;
        
        // Leave some padding at the bottom (20px) and ensure minimum height
        const maxHeight = Math.max(80, distanceToBottom - 20);

        // Set height based on content, but respect max height
        if (scrollHeight <= maxHeight) {
          textarea.style.height = scrollHeight + 'px';
          textarea.style.overflowY = 'hidden';
        } else {
          textarea.style.height = maxHeight + 'px';
          textarea.style.overflowY = 'auto';
        }
      };

      useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
          adjustTextareaHeight();
        }

        // If we have a trigger character, update the cell immediately
        if (cell.triggerChar) {
          // Update the cell state for ReactGrid
          const updatedCell = template.getCompatibleCell({ ...cell, text: initialValue, triggerChar: undefined });
          onCellChanged(updatedCell, false);
          
          // Call real-time change callback for row toolbar
          if (cell.onRealTimeChange) {
            cell.onRealTimeChange(initialValue);
          }
        }

        // Handle window resize to update max height
        const handleResize = () => {
          adjustTextareaHeight();
        };

        window.addEventListener('resize', handleResize);
        
        // Cleanup event listener
        return () => {
          window.removeEventListener('resize', handleResize);
        };
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const handleInputChange = (newValue: string) => {
        // Ensure newValue is a string
        const safeValue = typeof newValue === 'string' ? newValue : '';
        
        // Update the cell state for ReactGrid
        onCellChanged(
          template.getCompatibleCell({ ...cell, text: safeValue }),
          false
        );
        
        // Call real-time change callback for row toolbar
        if (cell.onRealTimeChange) {
          cell.onRealTimeChange(safeValue);
        }
        
        // Adjust height after content change
        setTimeout(() => {
          if (textareaRef.current) {
            adjustTextareaHeight();
          }
        }, 0);
      };

      return (
        <textarea
          ref={textareaRef}
          className="rg-textarea-editor"
          defaultValue={initialValue}
          onChange={e => {
            const target = e.currentTarget;
            if (target && target.value !== undefined) {
              handleInputChange(target.value);
            }
          }}
          onBlur={e => {
            const target = e.currentTarget;
            if (target && target.value !== undefined) {
              onCellChanged(
                template.getCompatibleCell({ ...cell, text: target.value }),
                true
              );
            }
          }}
          onCopy={e => e.stopPropagation()}
          onCut={e => e.stopPropagation()}
          onPaste={e => {
            e.stopPropagation();
            // Adjust height after paste and trigger real-time update
            setTimeout(() => {
              const textarea = textareaRef.current;
              if (textarea && textarea.value !== undefined) {
                handleInputChange(textarea.value);
              }
            }, 0);
          }}
          onPointerDown={e => e.stopPropagation()}
          onKeyUp={e => {
            // Adjust height after key input
            adjustTextareaHeight();
          }}
          onKeyDown={e => {
            const textarea = e.currentTarget as HTMLTextAreaElement;

            if (e.key === 'z' && (e.metaKey || e.ctrlKey)) {
              e.stopPropagation();
              return;
            }
            
            // Handle arrow keys for cursor movement within textarea
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
              e.stopPropagation();
              // Let the default behavior happen (move cursor within textarea)
              return;
            }
            
            // Handle Cmd+A/Ctrl+A for select all text in textarea
            if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
              e.stopPropagation();
              // Let the default behavior happen (select all text in textarea)
              return;
            }
            
            // Handle common editing keys within textarea
            if (['Backspace', 'Delete', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
              e.stopPropagation();
              // Let the default behavior happen
              return;
            }
            
            // Handle text selection with Shift + arrow keys
            if (e.shiftKey && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
              e.stopPropagation();
              // Let the default behavior happen (text selection)
              return;
            }
            
            // Only handle Shift+Enter or Cmd+Enter for new lines
            if (e.key === 'Enter' && (e.shiftKey || e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              e.stopPropagation();
              
              if (!textarea || textarea.value === undefined) return;
              
              // Manually insert newline at cursor position
              const start = textarea.selectionStart;
              const end = textarea.selectionEnd;
              const value = textarea.value;
              const newValue = value.substring(0, start) + '\n' + value.substring(end);
              
              textarea.value = newValue;
              textarea.setSelectionRange(start + 1, start + 1);
              
              // Trigger change event with real-time update
              handleInputChange(newValue);
              return;
            }
            
            // Let ReactGrid handle Enter and Escape for cell navigation
            if (['Enter', 'Escape'].includes(e.key)) {
              // Don't stop propagation - let ReactGrid handle these
              return;
            }
            
            // For all other keys, stop propagation to prevent ReactGrid from handling them
            e.stopPropagation();
          }}
          rows={1}
          cols={30}
        />
      );
    };

    return <TextAreaEditor />;
  }
}; 